import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessagesSquare, Trash2, RotateCcw, CheckCheck, List, Layers } from "lucide-react";
import { BusApi } from "../lib/api";
import { qk } from "../lib/query";
import { useStreamEvent } from "../lib/sse";
import { useToast } from "../lib/toast";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState } from "../components/primitives";
import { KindBadge } from "../components/badges";
import { PageHeader } from "../components/PageScaffold";
import { relativeTime, shortTime } from "../lib/format";
import { cn } from "../lib/cn";
import type { BusMessage } from "../lib/types";

type ViewMode = "list" | "threads";

/** A conversation: all loaded messages sharing a thread_id, or a single
 *  thread-less message on its own. Grouped client-side over the loaded set. */
interface Thread {
  key: string;
  messages: BusMessage[]; // oldest → newest
  latest: BusMessage;
  count: number;
  unread: number;
  participants: string[];
}

function groupThreads(messages: BusMessage[]): Thread[] {
  const groups = new Map<string, BusMessage[]>();
  for (const m of messages) {
    // A conversation's anchor is its ROOT message id. Replies/acks carry
    // thread_id = that root id — but a root itself may have thread_id = null
    // (e.g. a `notify`), so fall back to its own id: that's the id its replies
    // already point at, so both land in the same group. (`request` roots seed
    // thread_id = own id, so they anchor the same way.)
    const key = m.thread_id ?? m.reply_to_id ?? m.id;
    const arr = groups.get(key);
    if (arr) arr.push(m);
    else groups.set(key, [m]);
  }
  const threads: Thread[] = [];
  for (const [key, msgs] of groups) {
    const sorted = [...msgs].sort((a, b) => a.sent_at.localeCompare(b.sent_at));
    const latest = sorted[sorted.length - 1];
    const participants = Array.from(new Set(sorted.flatMap((m) => [m.from_agent, m.to_agent])));
    threads.push({
      key,
      messages: sorted,
      latest,
      count: sorted.length,
      unread: sorted.filter((m) => !m.read_at).length,
      participants,
    });
  }
  // Newest activity first (matches the flat list's newest-first ordering).
  threads.sort((a, b) => b.latest.sent_at.localeCompare(a.latest.sent_at));
  return threads;
}

export default function BusInbox() {
  const qc = useQueryClient();
  const toast = useToast();
  const [to, setTo] = useState<string>("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(
    () => (localStorage.getItem("busInboxView") === "threads" ? "threads" : "list"),
  );
  // Switching view changes what `selectedId` means (message id vs thread key),
  // so clear the selection to avoid a stale detail pane.
  const setViewMode = (v: ViewMode) => {
    setView(v);
    localStorage.setItem("busInboxView", v);
    setSelectedId(null);
  };

  const { data: agents } = useQuery({ queryKey: qk.bus.agents, queryFn: BusApi.agents });
  const { data: messages = [], isLoading, error } = useQuery({
    queryKey: qk.bus.messages({ to, unreadOnly, showScheduler }),
    queryFn: () => BusApi.messages({
      to: to || undefined,
      unread_only: unreadOnly,
      include_scheduler: showScheduler,
      limit: 200,
    }),
  });
  const { data: stats } = useQuery({ queryKey: qk.bus.stats, queryFn: BusApi.stats });

  const threads = useMemo(() => groupThreads(messages), [messages]);

  // Live refresh on SSE bus events
  useStreamEvent("bus", () => {
    qc.invalidateQueries({ queryKey: ["bus", "messages"] });
    qc.invalidateQueries({ queryKey: qk.bus.stats });
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["bus", "messages"] });
    qc.invalidateQueries({ queryKey: qk.bus.stats });
  };

  const markRead = useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) => BusApi.markRead(id, read),
    onSuccess: invalidate,
    onError: (err) => toast.error("Failed to update", String(err)),
  });
  const remove = useMutation({
    mutationFn: BusApi.deleteMessage,
    onSuccess: () => {
      setSelectedId(null);
      invalidate();
      toast.success("Message deleted");
    },
    onError: (err) => toast.error("Delete failed", String(err)),
  });
  const markAllRead = useMutation({
    mutationFn: async () => {
      const unread = messages.filter((m) => !m.read_at);
      for (const m of unread) await BusApi.markRead(m.id, true);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Marked all as read");
    },
  });
  const markThreadRead = useMutation({
    mutationFn: async (msgs: BusMessage[]) => {
      for (const m of msgs) if (!m.read_at) await BusApi.markRead(m.id, true);
    },
    onSuccess: invalidate,
    onError: (err) => toast.error("Failed to update", String(err)),
  });

  const toolbar = (
    <div className="flex items-center gap-2 px-3 py-2">
      <ViewToggle view={view} onChange={setViewMode} />
      <select
        value={to}
        onChange={(e) => setTo(e.target.value)}
        className="h-7 text-xs w-32"
      >
        <option value="">All agents</option>
        {agents?.map((a) => (
          <option key={a.name} value={a.name}>{a.name}{a.unread > 0 ? ` (${a.unread})` : ""}</option>
        ))}
      </select>
      <label className="flex items-center gap-1.5 text-xs text-fg-muted cursor-pointer select-none">
        <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="size-3.5" />
        unread only
      </label>
      <label className="flex items-center gap-1.5 text-xs text-fg-muted cursor-pointer select-none">
        <input type="checkbox" checked={showScheduler} onChange={(e) => setShowScheduler(e.target.checked)} className="size-3.5" />
        show scheduler fires
      </label>
      <span className="ml-auto text-[11px] text-fg-subtle font-mono">
        {view === "threads" ? `${threads.length} thread${threads.length === 1 ? "" : "s"}` : messages.length}
      </span>
    </div>
  );

  const emptyState = (
    <EmptyState
      icon={<MessagesSquare className="size-8" />}
      title="Nothing here"
      description="The mailbox is empty for this filter."
    />
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Bus Inbox"
        icon={MessagesSquare}
        subtitle={
          stats
            ? `${stats.total} total · ${stats.unread} unread · ${stats.scheduled} scheduled · ${stats.scheduled_tasks ?? 0} tasks`
            : "internal agent-to-agent mail"
        }
        actions={
          <Button size="sm" variant="secondary" onClick={() => markAllRead.mutate()} loading={markAllRead.isPending}>
            <CheckCheck className="size-3.5" />
            Mark all read
          </Button>
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        {view === "threads" ? (
          <ListDetail<Thread>
            items={threads}
            itemKey={(t) => t.key}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={isLoading}
            error={error as Error | null}
            itemHeight={72}
            toolbar={toolbar}
            emptyState={emptyState}
            renderItem={({ item, isSelected }) => <ThreadRow thread={item} active={isSelected} />}
            renderDetail={(t) => (
              <ThreadDetail
                thread={t}
                onMarkThreadRead={() => markThreadRead.mutate(t.messages)}
                onToggleRead={(m) => markRead.mutate({ id: m.id, read: !m.read_at })}
                onDelete={(m) => {
                  if (confirm("Delete this message?")) remove.mutate(m.id);
                }}
              />
            )}
          />
        ) : (
          <ListDetail<BusMessage>
            items={messages}
            itemKey={(m) => m.id}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={isLoading}
            error={error as Error | null}
            itemHeight={68}
            toolbar={toolbar}
            emptyState={emptyState}
            renderItem={({ item, isSelected }) => <BusRow message={item} active={isSelected} />}
            renderDetail={(m) => (
              <BusDetail
                message={m}
                onToggleRead={() => markRead.mutate({ id: m.id, read: !m.read_at })}
                onDelete={() => {
                  if (confirm("Delete this message?")) remove.mutate(m.id);
                }}
              />
            )}
          />
        )}
      </div>
    </div>
  );
}

function ViewToggle({ view, onChange }: { view: ViewMode; onChange: (v: ViewMode) => void }) {
  const opts: Array<{ v: ViewMode; icon: typeof List; label: string }> = [
    { v: "list", icon: List, label: "List view" },
    { v: "threads", icon: Layers, label: "Group by thread" },
  ];
  return (
    <div className="flex items-center rounded-md border border-border overflow-hidden flex-shrink-0" role="group" aria-label="View mode">
      {opts.map(({ v, icon: Icon, label }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            "flex items-center justify-center size-7 transition-colors",
            view === v ? "bg-accent/15 text-accent" : "text-fg-muted hover:text-fg",
          )}
          aria-pressed={view === v}
          aria-label={label}
          title={label}
        >
          <Icon className="size-3.5" />
        </button>
      ))}
    </div>
  );
}

function BusRow({ message, active }: { message: BusMessage; active: boolean }) {
  const unread = !message.read_at;
  return (
    <div className={cn("relative px-3 py-2.5", active ? "" : "")}>
      {unread && (
        <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent" aria-hidden />
      )}
      <div className="flex items-center gap-2 mb-1 min-w-0">
        <KindBadge kind={message.kind} />
        <span className={cn("text-sm font-medium truncate", unread ? "text-fg" : "text-fg-muted")}>
          {message.from_agent} <span className="text-fg-subtle">→</span> {message.to_agent}
        </span>
        <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">
          {relativeTime(message.sent_at).replace(" ago", "")}
        </span>
      </div>
      <div className={cn("text-xs truncate", unread ? "text-fg" : "text-fg-muted")}>
        {message.subject ?? <span className="italic text-fg-subtle">(no subject)</span>}
      </div>
    </div>
  );
}

function ThreadRow({ thread, active }: { thread: Thread; active: boolean }) {
  const { latest, count, unread, participants } = thread;
  return (
    <div className={cn("relative px-3 py-2.5", active ? "" : "")}>
      {unread > 0 && (
        <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent" aria-hidden />
      )}
      <div className="flex items-center gap-2 mb-1 min-w-0">
        <KindBadge kind={latest.kind} />
        <span className={cn("text-sm font-medium truncate", unread > 0 ? "text-fg" : "text-fg-muted")}>
          {participants.join(" · ")}
        </span>
        {count > 1 && <Pill tone="neutral">{count}</Pill>}
        <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">
          {relativeTime(latest.sent_at).replace(" ago", "")}
        </span>
      </div>
      <div className={cn("text-xs truncate", unread > 0 ? "text-fg" : "text-fg-muted")}>
        {latest.subject ?? <span className="italic text-fg-subtle">(no subject)</span>}
        {unread > 0 && <span className="text-accent"> · {unread} unread</span>}
      </div>
    </div>
  );
}

function BusDetail({
  message,
  onToggleRead,
  onDelete,
}: {
  message: BusMessage;
  onToggleRead: () => void;
  onDelete: () => void;
}) {
  const unread = !message.read_at;
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-fg-muted">
            <KindBadge kind={message.kind} />
            <span>{message.from_agent}</span>
            <span className="text-fg-subtle">→</span>
            <span>{message.to_agent}</span>
            {message.requires_ack === 1 && <Pill tone="danger">ack required</Pill>}
            {message.thread_id && <Pill tone="info">thread</Pill>}
          </div>
          <h2 className="text-base font-semibold text-fg mt-1.5">
            {message.subject ?? <span className="italic text-fg-subtle">(no subject)</span>}
          </h2>
          <p className="text-xs text-fg-muted mt-0.5">
            sent {relativeTime(message.sent_at)} · {shortTime(message.sent_at)}
            {message.read_at && <> · read {relativeTime(message.read_at)}</>}
            {message.notified_at && <> · notified {relativeTime(message.notified_at)}</>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Button size="sm" variant="secondary" onClick={onToggleRead}>
            <RotateCcw className="size-3.5" />
            {unread ? "Mark read" : "Mark unread"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete">
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      {/* Where this came from — resolved chat / route / schedule */}
      <MessageContextCard id={message.id} />

      {/* Body */}
      <div className="px-5 py-4">
        <pre className="font-sans text-sm text-fg whitespace-pre-wrap [overflow-wrap:anywhere] leading-relaxed">{message.body}</pre>
      </div>

      {/* Source */}
      {message.source && (
        <div className="px-5 pb-5">
          <details className="panel">
            <summary className="px-3 py-2 text-xs font-medium text-fg-muted cursor-pointer">
              Source metadata
            </summary>
            <pre className="px-3 py-2 text-[11px] font-mono text-fg overflow-auto max-h-[40vh] border-t border-border">
              {JSON.stringify(message.source, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

// The list endpoint doesn't resolve context (a per-row DB join); fetch the
// detail once a message is opened and show where it actually came from.
function MessageContextCard({ id }: { id: string }) {
  const { data } = useQuery({ queryKey: ["bus-msg-ctx", id], queryFn: () => BusApi.message(id) });
  const ctx = data?.context;
  if (!ctx || (!ctx.chat && !ctx.schedule)) return null;
  const chat = ctx.chat;
  return (
    <div className="px-5 pt-3">
      <div className="panel px-3 py-2.5 space-y-1.5">
        {chat && (
          <>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              {chat.platform && <Pill tone="info">{chat.platform}</Pill>}
              {chat.kind && <Pill tone="neutral">{chat.kind}</Pill>}
              {chat.observe && <Pill tone="warning">observer</Pill>}
              <span className="text-fg font-medium">{chat.title ?? chat.chat_id}</span>
              {chat.title && <span className="font-mono text-[10px] text-fg-subtle">{chat.chat_id}</span>}
            </div>
            <div className="text-xs text-fg-muted">
              {chat.channel && <>channel <span className="font-mono text-fg">{chat.channel}</span></>}
              {chat.route && <> · route <span className="font-mono text-fg">{chat.route}</span></>}
              {chat.route_persona && <> · persona <span className="text-fg">{chat.route_persona}</span></>}
            </div>
            {chat.participants.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap text-[11px] text-fg-muted">
                <span className="text-fg-subtle">people:</span>
                {chat.participants.map((p) => (
                  <span key={p.id} className="px-1.5 rounded bg-surface-2 text-fg">{p.name}</span>
                ))}
              </div>
            )}
          </>
        )}
        {ctx.schedule && (
          <div className="text-xs text-fg-muted">
            schedule <span className="font-mono text-fg">{ctx.schedule.name ?? ctx.schedule.id}</span>
            {ctx.schedule.target_agent && <> · fires <span className="text-fg">{ctx.schedule.target_agent}</span></>}
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadDetail({
  thread,
  onMarkThreadRead,
  onToggleRead,
  onDelete,
}: {
  thread: Thread;
  onMarkThreadRead: () => void;
  onToggleRead: (m: BusMessage) => void;
  onDelete: (m: BusMessage) => void;
}) {
  const { messages, latest, count, unread, participants } = thread;
  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-fg-muted">
            <span className="truncate">{participants.join(" · ")}</span>
            <Pill tone="neutral">{count} message{count === 1 ? "" : "s"}</Pill>
            {unread > 0 && <Pill tone="info">{unread} unread</Pill>}
          </div>
          <h2 className="text-base font-semibold text-fg mt-1.5">
            {latest.subject ?? <span className="italic text-fg-subtle">(no subject)</span>}
          </h2>
        </div>
        {unread > 0 && (
          <div className="flex-shrink-0">
            <Button size="sm" variant="secondary" onClick={onMarkThreadRead}>
              <CheckCheck className="size-3.5" />
              Mark thread read
            </Button>
          </div>
        )}
      </div>

      {/* Where this thread came from */}
      <MessageContextCard id={messages[0].id} />

      {/* Conversation, oldest → newest */}
      <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-3">
        {messages.map((m) => (
          <ThreadMessage key={m.id} message={m} onToggleRead={() => onToggleRead(m)} onDelete={() => onDelete(m)} />
        ))}
      </div>
    </div>
  );
}

function ThreadMessage({
  message,
  onToggleRead,
  onDelete,
}: {
  message: BusMessage;
  onToggleRead: () => void;
  onDelete: () => void;
}) {
  const unread = !message.read_at;
  return (
    <div className={cn("panel px-3 py-2.5", unread && "ring-1 ring-accent/40")}>
      <div className="flex items-center gap-2 mb-1.5 text-xs text-fg-muted min-w-0">
        <KindBadge kind={message.kind} />
        <span className="truncate">
          {message.from_agent} <span className="text-fg-subtle">→</span> {message.to_agent}
        </span>
        {message.requires_ack === 1 && <Pill tone="danger">ack</Pill>}
        <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[11px] text-fg-subtle font-mono whitespace-nowrap" title={shortTime(message.sent_at)}>
            {relativeTime(message.sent_at)}
          </span>
          <button
            type="button"
            onClick={onToggleRead}
            className="text-fg-subtle hover:text-fg"
            title={unread ? "Mark read" : "Mark unread"}
          >
            <RotateCcw className="size-3.5" />
          </button>
          <button type="button" onClick={onDelete} className="text-fg-subtle hover:text-danger" title="Delete">
            <Trash2 className="size-3.5" />
          </button>
        </span>
      </div>
      {message.subject && (
        <div className="text-sm font-medium text-fg mb-1">{message.subject}</div>
      )}
      <pre className="font-sans text-sm text-fg whitespace-pre-wrap [overflow-wrap:anywhere] leading-relaxed">{message.body}</pre>
      {message.source && (
        <details className="mt-2">
          <summary className="text-[11px] font-medium text-fg-subtle cursor-pointer">Source metadata</summary>
          <pre className="mt-1 text-[11px] font-mono text-fg overflow-auto max-h-[30vh]">
            {JSON.stringify(message.source, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
